import { useEffect, useState, type SyntheticEvent } from "react";

/**
 * Sized, compressed image URLs for Supabase Storage objects, via Cloudflare
 * Image Transformations on a dedicated image host (`VITE_IMAGE_CDN_URL`).
 *
 * Generated images are stored as full-resolution PNGs (a cover is ~1 MB, a
 * portrait ~2 MB), yet most surfaces show them as a card, a blurred backdrop or
 * a 20px avatar. `https://<host>/cdn-cgi/image/<options>/<source>` resizes on
 * the fly and serves AVIF/WebP to browsers that accept it, so a card cover
 * drops from ~900 KB to ~15 KB. Anything that is not a public Supabase object
 * (placeholders, data URLs, bundled assets) is returned unchanged, as is every
 * URL when the build has no image host (local dev).
 *
 * A separate host rather than the site's own: the site's DNS record points at
 * Render un-proxied, so its requests never reach the Lorvok Cloudflare zone
 * where transformations are enabled. The image host is a proxied, origin-less
 * record in that zone — it only ever serves `/cdn-cgi/image/`.
 *
 * Cloudflare rather than Supabase's own render endpoint: it is several times
 * cheaper per image, and it can keep the images' C2PA "AI-generated" content
 * credentials (the "Preserve Content Credentials" zone setting) where Supabase
 * strips all metadata — see apps/api/src/aiProvenance.ts for why they matter.
 *
 * Widths snap to a fixed ladder: Cloudflare bills each distinct size of an
 * image, and two surfaces of similar size then share one variant (and one CDN
 * cache entry). Neither fit mode ever upscales, so a step above the source
 * width just returns the original dimensions.
 */

const OBJECT_SEGMENT = "/storage/v1/object/public/";
const WIDTH_STEPS = [64, 128, 256, 480, 640, 960, 1280, 1600, 1920];
const DEFAULT_QUALITY = 75;

/**
 * The one variant every cover surface shares: film cards (≈200–230px wide),
 * the novel poster (≈300px), the generation dialog thumbnail and the blurred
 * backdrops. All are 2:3 boxes painted with `object-fit: cover`, so one
 * centre crop serves them from a single cache entry.
 */
export const POSTER_CSS_WIDTH = 320;
export const POSTER_ASPECT = 1.5;

type SizeOptions = { aspect?: number; density?: number; quality?: number };
type ImageSizer = {
  (url: string, cssWidth: number, options?: SizeOptions): string;
  (url: string | null | undefined, cssWidth: number, options?: SizeOptions): string | null | undefined;
};

/** Device pixel ratio, capped at 2 — sharper than that is invisible but costly. */
export function imageDensity(): number {
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  return Math.min(Math.max(dpr, 1), 2);
}

/**
 * A sizer that routes through `transformOrigin`'s `/cdn-cgi/image/`, or one
 * that returns every URL unchanged when there is no (valid) origin.
 */
export function createImageSizer(transformOrigin: string | undefined): ImageSizer {
  let origin: string | null = null;
  try {
    origin = transformOrigin ? new URL(transformOrigin).origin : null;
  } catch {
    origin = null;
  }
  function sized(
    url: string | null | undefined,
    cssWidth: number,
    options: SizeOptions = {}
  ): string | null | undefined {
    if (!origin || !url || !url.startsWith("https://") || !url.includes(OBJECT_SEGMENT)) return url;
    const target = Math.ceil(cssWidth * (options.density ?? imageDensity()));
    const width = WIDTH_STEPS.find((step) => step >= target) ?? WIDTH_STEPS[WIDTH_STEPS.length - 1];
    // A box of fixed proportions painted with `object-fit: cover` gets exactly
    // the centre crop the browser would have shown (`crop` = cover without
    // upscaling); everything else scales to the width, aspect kept.
    const box = options.aspect
      ? [`width=${width}`, `height=${Math.round(width * options.aspect)}`, "fit=crop"]
      : [`width=${width}`, "fit=scale-down"];
    const params = [...box, `quality=${options.quality ?? DEFAULT_QUALITY}`, "format=auto"];
    return `${origin}/cdn-cgi/image/${params.join(",")}/${url}`;
  }
  return sized as ImageSizer;
}

/**
 * `url` resized to `cssWidth` CSS pixels at `density` (defaults to the device's,
 * capped at 2). Pass `aspect` (height / width) for a box of fixed proportions
 * shown with `object-fit: cover`, and `density: 1` for images that are blurred
 * or otherwise don't need full sharpness.
 */
export const sizedImageUrl = createImageSizer(import.meta.env.VITE_IMAGE_CDN_URL as string | undefined);

/**
 * `onError` for an `<img>` showing a sized URL: falls back to the original
 * once, so a transformation failure (the zone setting switched off, the free
 * monthly quota spent) degrades to a slower image instead of a broken one.
 */
export function fallBackToSource(source: string | null | undefined) {
  return (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    if (source && image.getAttribute("src") !== source) image.src = source;
  };
}

/**
 * The CSS-background counterpart of `fallBackToSource`: a background image has
 * no `onError`, so probe the sized URL (the browser shares the request with the
 * background's own) and switch to the original if it fails.
 */
export function useSizedBackground(
  source: string | null | undefined,
  sized: string | null | undefined
): string | null | undefined {
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    if (!sized || sized === source) return;
    let active = true;
    const probe = new Image();
    probe.onerror = () => {
      if (active) setFailed(sized);
    };
    probe.src = sized;
    return () => {
      active = false;
    };
  }, [source, sized]);
  return sized && failed === sized ? source : sized;
}
